import { useEffect } from 'react';
import { useSessionStore } from '@/store/sessionStore';

/**
 * 顶部状态条：显示连接状态 / 存储兜底提示 / 屏幕常亮提示 / 错误 toast / session reset
 */
export function StatusBar(): JSX.Element {
  const status = useSessionStore((s) => s.connectionStatus);
  const storageMode = useSessionStore((s) => s.storageMode);
  const phase = useSessionStore((s) => s.phase);
  const lastError = useSessionStore((s) => s.lastError);
  const clearLastError = useSessionStore((s) => s.clearLastError);
  const resetReason = useSessionStore((s) => s.sessionResetReason);
  const clearReset = useSessionStore((s) => s.clearSessionReset);
  const startCancelReason = useSessionStore((s) => s.startCancelReason);
  const clearStartCancelReason = useSessionStore((s) => s.clearStartCancelReason);

  useEffect(() => {
    if (!lastError) return;
    const id = setTimeout(() => clearLastError(), 3000);
    return () => clearTimeout(id);
  }, [lastError, clearLastError]);

  useEffect(() => {
    if (!resetReason) return;
    const id = setTimeout(() => clearReset(), 4000);
    return () => clearTimeout(id);
  }, [resetReason, clearReset]);

  useEffect(() => {
    if (!startCancelReason) return;
    const id = setTimeout(() => clearStartCancelReason(), 4000);
    return () => clearTimeout(id);
  }, [startCancelReason, clearStartCancelReason]);

  const showStayAwake = phase === 'lobby' || phase === 'night' || phase === 'day';

  return (
    <div data-testid="status-bar" className="sticky top-0 z-30 space-y-1">
      {status !== 'open' && (
        <div
          role="status"
          className={`px-3 py-2 text-sm text-white ${
            status === 'reconnecting' ? 'bg-yellow-500' : 'bg-gray-500'
          }`}
        >
          {status === 'connecting' && '连接中…'}
          {status === 'reconnecting' && '正在重连…'}
          {status === 'closed' && '连接已断开'}
          {status === 'idle' && '准备连接'}
        </div>
      )}
      {storageMode !== 'local' && (
        <div className="bg-orange-100 px-3 py-2 text-xs text-orange-800">
          ⚠️ 浏览器无法长期保存身份，刷新页面可能丢失角色（建议关闭隐私模式）
        </div>
      )}
      {showStayAwake && (
        <div className="bg-pig-50 px-3 py-1 text-center text-xs text-pig-700">
          📱 建议关掉屏幕自动锁，避免游戏中黑屏
        </div>
      )}
      {lastError && (
        <div
          role="alert"
          className="mx-2 rounded bg-red-100 px-3 py-2 text-sm text-red-800 shadow"
        >
          {humanizeError(lastError.code, lastError.message)}
        </div>
      )}
      {resetReason && (
        <div className="mx-2 rounded bg-blue-100 px-3 py-2 text-sm text-blue-800 shadow">
          管理员重置了游戏（{resetReason}）
        </div>
      )}
      {startCancelReason && (
        <div
          role="alert"
          data-testid="start-cancel-toast"
          className="mx-2 rounded bg-amber-100 px-3 py-2 text-sm text-amber-900 shadow"
        >
          {humanizeStartCancel(startCancelReason)}
        </div>
      )}
    </div>
  );
}

function humanizeError(code: string, fallback?: string): string {
  switch (code) {
    case 'VALIDATION_ERROR':
      return fallback || '输入不合法';
    case 'PHASE_NOT_ALLOWED':
      return '操作太晚了，已进入下一阶段';
    case 'LONG_PRESS_TOO_SHORT':
      return '长按时间不够 2 秒';
    case 'LONG_PRESS_NO_BEGIN':
      return '请先长按按钮';
    case 'NOT_CHIEF':
      return '只有村长可以这么做';
    case 'NOT_WOLF':
      return '只有狼可以这么做';
    case 'TARGET_NOT_IN_MATCH':
      return '目标玩家不在本局';
    case 'NOT_ENOUGH_PLAYERS':
      return '至少 2 人才能开局';
    case 'WAITLIST_FULL':
      return '场子已满 + 等待席已满';
    case 'ALREADY_SUBMITTED':
      return '你已经提交过了';
    case 'START_REQUEST_EXISTS':
      return '已有未结束的开局请求';
    case 'FORBIDDEN':
      return '没有权限';
    case 'RATE_LIMITED':
      return '操作太频繁，稍等一下';
    case 'INTERNAL_ERROR':
      return '服务器内部错误';
    default:
      return fallback || code;
  }
}

/**
 * START_REQUEST_CANCELLED.payload.reason 映射 — 覆盖后端
 * src/do/transitions.ts + handlers-lobby.ts + GameSessionDO.ts 中的全部 reason。
 */
function humanizeStartCancel(reason: string): string {
  switch (reason) {
    case 'no_words_available':
      return '词库为空，请联系管理员添加';
    case 'assign_invariant_broken':
      return '角色分配异常，本局已取消，请重试';
    case 'timeout':
      return '准备超时，已取消开局';
    case 'manual':
      return '发起人取消了开局';
    default:
      return `开局取消：${reason}`;
  }
}
